import PlanogramDialog from '@/components/associate/PlanogramDialog';
import ScannerInputField, { ScannerInputFieldRef } from '@/mobile/components/ScannerInputField';
import ImageDialog from '@/components/associate/ImageDialog';
import DefaultLayoutDialog from '@/components/associate/DefaultLayoutDialog';
import BaseFontIcon from '@/components/ui/BaseFontIcon';
import { useTranslation } from 'react-i18next';
import { useMobileState } from '@/mobile/provider/context';
import {
    useCallback,
    useRef,
    KeyboardEvent,
    useMemo,
    useReducer,
    useState,
    useImperativeHandle,
    forwardRef,
    Ref,
    useLayoutEffect,
} from 'react';
import { useAppContext } from '@/provider/contexts';
import { SETTING_FLAG, ERROR_CODE_MAP } from '@/mobile/utils/mobileUtils';
import { PushDeviceRequest } from '@/types/pages/storeManagement/device';
import { MobileFeatureHandle, MobileFeatureViewProps } from '@/mobile/types';
import { formatBarcode } from '@/utils/inputUtils';
import { fetchTagLayoutDetailsAPI, pushDevicesToProductsAPI } from '@/api/storeManagement/device';
import { fetchProductInfoAPI } from '@/api/storeManagement/shelfPlanogram';
import {
    splitLayoutsByCampaign,
    deriveModeConfig,
    parseRelationId,
    parseLpIdentifierInfo,
} from '@/utils/associateUtils';
import { defaultState, reducer, AssociationFlowContext, AssociationError } from './associationView.state';
import { createOnScanHandler } from '@/mobile/utils/createOnScanHandler';
import { createEslInputHandler } from '@/mobile/utils/createEslInputHandler';
import { Box, Checkbox, FormControlLabel, Radio, RadioGroup, Tooltip, Typography } from '@mui/material';
import { ASSOCIATE_PAIRING_MODE, ImageInfo, ShelfInfo } from '@/types/pages/storeManagement/device/associate';
import { getInitialPairingMode, isProductInputEnabledForMode } from '../multiAssociation/multiAssociationView.utils';
import { isArray } from 'lodash';

const AssociationView = forwardRef(({ dependency }: MobileFeatureViewProps, ref: Ref<MobileFeatureHandle>) => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state } = useMobileState();
    const [viewState, dispatchView] = useReducer(reducer, defaultState);
    const {
        settings,
        refs: { isSendingRef, eslMacInputRef },
        method: { focusCommonEslInput, setESL, setButtonInfo, addHistoryItem },
    } = dependency;

    const {
        product,
        singleLayoutInfo,
        defaultLayoutId,
        selectedPairingMode,
        planogramConfig,
        campaignEnable,
        layoutDialogOpen,
        planogramDialogOpen,
        imageDialogOpen,
        isProductInputEnabled,
        shelfInfo,
        imageInfo,
        identifierInfo,
        pendingAssociationContext,
    } = viewState;

    const { modeLayoutList, hasNormalModeLayout, hasImageModeLayout, hasAnyModeLayout, hasDisabledPairingOption } =
        useMemo(() => {
            const { campaign, nonCampaign } = splitLayoutsByCampaign(singleLayoutInfo);
            return {
                modeLayoutList: selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL ? nonCampaign : campaign,
                hasNormalModeLayout: nonCampaign.length > 0,
                hasImageModeLayout: campaignEnable && campaign.length > 0,
                hasAnyModeLayout: nonCampaign.length > 0 || (campaignEnable && campaign.length > 0),
                hasDisabledPairingOption: nonCampaign.length === 0 || (campaignEnable && campaign.length === 0),
            };
        }, [selectedPairingMode, campaignEnable, singleLayoutInfo]);

    const [pairingHintOpen, setPairingHintOpen] = useState(false);
    const [focusProductTick, setFocusProductTick] = useState(0);
    const productRef = useRef<ScannerInputFieldRef | null>(null);

    useLayoutEffect(() => {
        if (!isProductInputEnabled) return;

        productRef.current?.focus();
    }, [focusProductTick, isProductInputEnabled]);

    const focusProductInput = useCallback(() => {
        setFocusProductTick((prev) => prev + 1);
    }, []);

    const resetAssociationView = useCallback(() => {
        setESL('');
        productRef.current?.blur();
        dispatchView({ type: 'RESET_ALL' });
        setTimeout(() => {
            focusCommonEslInput();
        }, 150);
    }, []);

    const fetchTagInfo = useCallback(
        async (mac: string) => {
            isSendingRef.current = true;
            setButtonInfo({ type: 'loading' });

            try {
                const {
                    data: { data, isSuccess, status },
                } = await fetchTagLayoutDetailsAPI(manageStoreId as number, [mac]);

                if (isSuccess) {
                    const {
                        single_layouts,
                        is_planogram_enabled,
                        planogram_show_nooption,
                        planogram_autoselectsingleopt,
                        is_campaign_enabled,
                        lp_identifier,
                    } = data.device?.[0] || {};

                    const nextSingleLayouts = single_layouts || [];
                    const { campaign, nonCampaign } = splitLayoutsByCampaign(nextSingleLayouts);
                    const hasNormalLayouts = nonCampaign.length > 0;
                    const hasImageLayouts = is_campaign_enabled && campaign.length > 0;

                    if (!hasNormalLayouts && !hasImageLayouts) {
                        resetAssociationView();
                        setButtonInfo({ type: 'error', msg: 'No layout for ESL. Contact admin.' });
                        return;
                    }

                    const initialMode = getInitialPairingMode(nonCampaign, campaign);
                    const hasInitialModeLayout = isProductInputEnabledForMode(initialMode, nonCampaign, campaign);
                    const nextIdentifierInfo = parseLpIdentifierInfo(lp_identifier);

                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            singleLayoutInfo: nextSingleLayouts,
                            selectedPairingMode: initialMode,
                            defaultLayoutId: null,
                            planogramConfig: {
                                enable: is_planogram_enabled,
                                noOption: SETTING_FLAG[planogram_show_nooption] || false,
                                autoSelect: SETTING_FLAG[planogram_autoselectsingleopt] || false,
                            },
                            campaignEnable: is_campaign_enabled,
                            isProductInputEnabled: hasInitialModeLayout,
                            identifierInfo: nextIdentifierInfo,
                        },
                    });

                    setButtonInfo({ type: 'idle' });
                    focusProductInput();
                    return;
                }

                if (status === 400 || status === 401) {
                    const msg = ERROR_CODE_MAP[data.error_code] || null;
                    if (msg) {
                        resetAssociationView();
                        setButtonInfo({ type: 'error', msg });
                        return;
                    }
                }

                throw new Error();
            } catch (error: unknown) {
                if (error instanceof Error) {
                    const message = error.message;
                    resetAssociationView();
                    setNotification({ message, severity: 'error' });
                    setButtonInfo({ type: 'error', msg: message });
                    return;
                }
                console.error('Error in handleSingleLayoutAPI:', error);
            } finally {
                isSendingRef.current = false;
            }
        },
        [manageStoreId],
    );

    const handleAutomaticAssociation = useCallback(
        async (context: AssociationFlowContext) => {
            const { mac, productBarcode, relations } = context;

            if (!manageStoreId || !mac || !productBarcode) {
                return;
            }

            isSendingRef.current = true;
            setButtonInfo({ type: 'sending' });

            const payload: PushDeviceRequest = {
                store_id: manageStoreId,
                macs: [mac],
                products: [productBarcode],
                overwrite_layout: defaultLayoutId ?? 'default',
            };

            const relationKey = `${productBarcode}-0`;
            const relationValue = relations ? relations[relationKey] : null;

            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL) {
                if (relationValue) {
                    payload.overwrite_shelf_codes = [parseRelationId(relationValue)];
                }
            }

            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.IMAGE) {
                if (relationValue) {
                    payload.overwrite_campaign_id = [parseRelationId(relationValue)];
                }
            }

            try {
                const {
                    data: { data, isSuccess, message, status },
                } = await pushDevicesToProductsAPI(payload);
                const { product_information, error_code, pushed_barcodes, blocked } = data || {};
                const pushedProductBarcode = pushed_barcodes[0];

                const { name } = !isArray(product_information) ? product_information[pushedProductBarcode] : {};
                const productName = name || 'Empty Product';
                const containsEmptyProduct = productName === 'Empty Product';

                if (isSuccess) {
                    setButtonInfo({ type: 'success' });
                    addHistoryItem({
                        eslMac: mac,
                        productBarcode: pushedProductBarcode,
                        status: 'success',
                        timestamp: new Date().toLocaleString(),
                        productName,
                        allSuccess: true,
                        containsEmptyProduct,
                    });
                    resetAssociationView();
                    return;
                }

                const errorMessage = message.trim() || blocked?.[0]?.reason || 'Unknown error occurred';
                if (status === 400 || status === 401) {
                    throw new AssociationError(errorMessage, {
                        code: error_code,
                        status,
                        historyData: {
                            mac,
                            productBarcode: pushedProductBarcode,
                            productName,
                        },
                    });
                }

                throw new AssociationError(errorMessage, {
                    code: error_code,
                    status,
                });
            } catch (error) {
                if (error instanceof AssociationError) {
                    const { message, code, historyData } = error;

                    addHistoryItem({
                        eslMac: historyData?.mac ?? mac,
                        productBarcode: historyData?.productBarcode ?? productBarcode,
                        status: 'failed',
                        timestamp: new Date().toLocaleString(),
                        productName: historyData?.productName ?? 'Empty Product',
                        errorReason: code,
                        allSuccess: false,
                    });

                    resetAssociationView();
                    setNotification({ message, severity: 'error' });
                    setButtonInfo({ type: 'error', msg: 'failed' });
                    return;
                }

                const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
                resetAssociationView();
                setNotification({ message: errorMessage, severity: 'error' });
                setButtonInfo({ type: 'error', msg: 'failed' });
            } finally {
                isSendingRef.current = false;
            }
        },
        [manageStoreId, defaultLayoutId, selectedPairingMode],
    );

    const handleShelfFlow = useCallback(
        async (context: AssociationFlowContext, nextShelfInfo: Partial<ShelfInfo>) => {
            if (nextShelfInfo.shouldOpenDialog) {
                setButtonInfo({ type: 'idle' });
                dispatchView({
                    type: 'PATCH',
                    payload: {
                        planogramDialogOpen: true,
                        shelfInfo: { ...shelfInfo, ...nextShelfInfo },
                        pendingAssociationContext: context,
                    },
                });
                return;
            }

            await handleAutomaticAssociation({
                ...context,
                relations: nextShelfInfo.relations || {},
            });
        },
        [handleAutomaticAssociation, shelfInfo],
    );

    const handleNext = useCallback(
        async (
            context: AssociationFlowContext,
            nextShelfInfo: Partial<ShelfInfo>,
            nextImageInfo: Partial<ImageInfo>,
        ) => {
            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL) {
                if (nextShelfInfo.openFeature) {
                    await handleShelfFlow(context, nextShelfInfo);
                    return;
                }

                await handleAutomaticAssociation(context);
                return;
            }

            if (nextImageInfo.openFeature) {
                dispatchView({
                    type: 'PATCH',
                    payload: {
                        imageDialogOpen: true,
                        imageInfo: { ...imageInfo, ...nextImageInfo },
                        pendingAssociationContext: context,
                    },
                });
                return;
            }

            await handleAutomaticAssociation(context);
        },
        [handleAutomaticAssociation, handleShelfFlow, imageInfo, selectedPairingMode],
    );

    const fetchProductInfo = useCallback(
        async (context: AssociationFlowContext) => {
            isSendingRef.current = true;
            setButtonInfo({ type: 'loading' });

            try {
                const {
                    data: { data, isSuccess, message },
                } = await fetchProductInfoAPI(manageStoreId as number, [context.productBarcode]);
                if (isSuccess) {
                    const { nextShelfInfo, nextImageInfo } = deriveModeConfig(
                        data.results,
                        planogramConfig,
                        campaignEnable,
                        identifierInfo,
                    );
                    if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.IMAGE && !nextImageInfo.openFeature) {
                        setButtonInfo({ type: 'error', msg: 'No layout for ESL. Contact admin.' });
                        resetAssociationView();
                        return;
                    }

                    await handleNext(context, nextShelfInfo, nextImageInfo);
                    return;
                }

                throw new Error(message || 'Product fetch failed. Re-Scan.');
            } catch (error) {
                const errorReason = error instanceof Error ? error.message : 'Product fetch failed. Re-Scan.';
                setNotification({ message: errorReason, severity: 'error' });
                setButtonInfo({ type: 'error', msg: 'Failed' });
                resetAssociationView();
            } finally {
                isSendingRef.current = false;
            }
        },
        [manageStoreId, handleNext, campaignEnable, identifierInfo, planogramConfig, selectedPairingMode],
    );

    const handleProductInput = useCallback(
        async (rawValue: string) => {
            const inputValue = rawValue.trim();
            if (!inputValue || !isProductInputEnabled || state.isSubmitting || !state.esl) {
                return;
            }

            const processedBarcode = formatBarcode(
                settings.auto_convert_upce_to_upca === '1',
                settings.addLeadingZero === '1',
                inputValue,
            );

            dispatchView({ type: 'PATCH', payload: { product: processedBarcode } });

            const context: AssociationFlowContext = {
                mac: state.esl,
                productBarcode: processedBarcode,
            };

            await fetchProductInfo(context);
        },
        [
            fetchProductInfo,
            isProductInputEnabled,
            settings.addLeadingZero,
            settings.auto_convert_upce_to_upca,
            state.esl,
            state.isSubmitting,
        ],
    );

    const handleProductKeyDown = async (event: KeyboardEvent) => {
        if (isSendingRef.current || state.isSubmitting) return;
        if (event.key === 'Enter') {
            event.preventDefault();
            await handleProductInput((event.target as HTMLInputElement).value);
        }
    };

    const onEslEnter = useMemo(
        () =>
            createEslInputHandler({
                isSubmitting: state.isSubmitting,
                eslMacInputRef,
                setESL,
                setButtonInfo,
                onInvalid: resetAssociationView,
                onValid: fetchTagInfo,
            }),
        [eslMacInputRef, fetchTagInfo, resetAssociationView, setButtonInfo, setESL, state.isSubmitting],
    );

    const onScan = useMemo(
        () =>
            createOnScanHandler({
                shouldProcess: () => !state.isSubmitting,
                onNormalizedScan: async (normalized) => {
                    if (!state.esl) {
                        await onEslEnter(normalized);
                        return;
                    }

                    if (!isProductInputEnabled) {
                        return;
                    }

                    await handleProductInput(normalized);
                },
            }),
        [handleProductInput, isProductInputEnabled, onEslEnter, state.esl, state.isSubmitting],
    );

    const onModeChange = useCallback(() => {
        if (!state.keyboardEnabled) return;
        if (state.esl && isProductInputEnabled) {
            focusProductInput();
            return;
        }
        focusCommonEslInput();
    }, [isProductInputEnabled, state.esl, state.keyboardEnabled]);

    const onClear = useCallback(() => {
        resetAssociationView();
    }, []);

    // Implement methods that parent use.
    useImperativeHandle(
        ref,
        () => ({
            onEslEnter,
            onScan,
            onModeChange,
            onClear,
        }),
        [onEslEnter, onScan, onModeChange, onClear],
    );

    return (
        <>
            {singleLayoutInfo.length > 0 && hasAnyModeLayout && (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.78rem' }}>
                            {t('Pairing Mode')}
                        </Typography>

                        {hasDisabledPairingOption && (
                            <Tooltip
                                title={t('Disabled: no layout')}
                                placement="top"
                                open={pairingHintOpen}
                                onClose={() => setPairingHintOpen(false)}
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            fontSize: '0.72rem',
                                        },
                                    },
                                }}
                                arrow
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        pl: 0.5,
                                    }}
                                    onClick={() => setPairingHintOpen((prev) => !prev)}
                                >
                                    <BaseFontIcon color="info" className="fa-regular fa-circle-info" size={16} />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>

                    <RadioGroup
                        row
                        value={selectedPairingMode}
                        onChange={(event) => {
                            const mode = event.target.value as ASSOCIATE_PAIRING_MODE;

                            dispatchView({
                                type: 'PATCH',
                                payload: {
                                    selectedPairingMode: mode,
                                    isProductInputEnabled:
                                        mode === ASSOCIATE_PAIRING_MODE.NORMAL
                                            ? hasNormalModeLayout
                                            : hasImageModeLayout,
                                },
                            });

                            if (mode === ASSOCIATE_PAIRING_MODE.IMAGE) {
                                dispatchView({
                                    type: 'PATCH',
                                    payload: { defaultLayoutId: null },
                                });
                            }
                            productRef.current?.focus();
                        }}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', fontWeight: 700 } }}
                    >
                        <FormControlLabel
                            value={ASSOCIATE_PAIRING_MODE.NORMAL}
                            control={<Radio />}
                            label={t('Normal')}
                            disabled={!hasNormalModeLayout}
                        />
                        {campaignEnable && (
                            <FormControlLabel
                                value={ASSOCIATE_PAIRING_MODE.IMAGE}
                                control={<Radio />}
                                label={t('Image')}
                                disabled={!hasImageModeLayout}
                            />
                        )}
                    </RadioGroup>

                    {selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL && (
                        <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { m: 0, fontSize: '0.8rem', fontWeight: 700 } }}
                            control={
                                <Checkbox
                                    disabled={isSendingRef.current}
                                    checked={defaultLayoutId === null}
                                    onChange={() => {
                                        if (!defaultLayoutId) {
                                            dispatchView({
                                                type: 'PATCH',
                                                payload: { layoutDialogOpen: true },
                                            });
                                            return;
                                        }

                                        dispatchView({
                                            type: 'PATCH',
                                            payload: { defaultLayoutId: null },
                                        });
                                    }}
                                />
                            }
                            label={t('Use Default Layout')}
                        />
                    )}
                </Box>
            )}

            <Box data-testid="association-view-root">
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('Product Barcodes')}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '0.5rem' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 60,
                            borderRadius: 1,
                            backgroundColor: '#a3c585',
                            color: 'white',
                            fontWeight: 700,
                        }}
                    >
                        1
                    </Box>

                    <ScannerInputField
                        ref={productRef}
                        value={product}
                        setValue={(value) => dispatchView({ type: 'PATCH', payload: { product: value } })}
                        disabled={!isProductInputEnabled}
                        onKeyDown={handleProductKeyDown}
                        onScannerScan={handleProductInput}
                        inputTestId="association-product-input-0"
                    />
                </Box>
            </Box>

            <DefaultLayoutDialog
                eslMac={state.esl}
                open={layoutDialogOpen}
                options={modeLayoutList.map((o) => ({
                    id: o.layout_id,
                    name: o.layout_name,
                    previewUrl: o.preview,
                }))}
                onConfirm={(id) =>
                    dispatchView({
                        type: 'PATCH',
                        payload: { layoutDialogOpen: false, defaultLayoutId: id },
                    })
                }
                onClose={() => dispatchView({ type: 'PATCH', payload: { layoutDialogOpen: false } })}
                onExited={focusProductInput}
            />

            <PlanogramDialog
                open={planogramDialogOpen}
                onClose={() => {
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            planogramDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });
                    focusProductInput();
                    setButtonInfo({ type: 'idle' });
                }}
                planogramInfo={shelfInfo.options}
                relations={shelfInfo.relations}
                handleOnClick={async (relations) => {
                    const context = pendingAssociationContext;
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            planogramDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });

                    if (!context) {
                        return;
                    }

                    await handleAutomaticAssociation({
                        ...context,
                        relations,
                    });
                }}
            />

            <ImageDialog
                open={imageDialogOpen}
                onClose={() => {
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            imageDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });
                    focusProductInput();
                    setButtonInfo({ type: 'idle' });
                }}
                options={imageInfo.options}
                relations={imageInfo.relations}
                handleOnClick={async (relations) => {
                    const context = pendingAssociationContext;
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            imageDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });

                    if (!context) {
                        return;
                    }

                    await handleAutomaticAssociation({
                        ...context,
                        relations,
                    });
                }}
            />
        </>
    );
});

export default AssociationView;
